import React, { useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { useFlowStore } from "../../store/FlowStateStore.ts";
import { CanvasNode, CanvasNodeData } from "../../models/CanvasNode.ts";
import AddProductDialog, { CatalogProduct } from "./AddProductDialog.tsx";
import AttributePicker from "../Common/AttributePicker.tsx";
import { Plus, Trash2 } from "lucide-react";

// Meta's caps on a multi-product message. Mirrored in WhatsappConstants on the backend.
const MAX_SECTIONS = 10;
const MAX_PRODUCTS = 30;
const MAX_TITLE = 24;

interface Section {
    title: string;
    products: CatalogProduct[];
}

const ProductListDialog: React.FC<any> = ({ data, onClose }) => {
    const { nodes, setNodes, businessId } = useFlowStore();

    const [header, setHeader] = useState("");
    const [body, setBody] = useState("");
    const [footer, setFooter] = useState("");
    const [saveAs, setSaveAs] = useState("");
    const [catalogId, setCatalogId] = useState("");
    const [sections, setSections] = useState<Section[]>([{ title: "", products: [] }]);
    const [pickerFor, setPickerFor] = useState<number | null>(null);

    useEffect(() => {
        const node = nodes.find((n) => n.data.id === data.nodeId);
        if (!node) return;
        const content = (node.data as CanvasNodeData).content.find((c) => c.id === data.subNodeId);
        if (!content?.data) return;
        const d = content.data as any;
        setHeader(d.header || "");
        setBody(d.body || "");
        setFooter(d.footer || "");
        setSaveAs(d.saveAs || "");
        setCatalogId(d.catalogId || "");
        setSections(d.sections?.length ? d.sections : [{ title: "", products: [] }]);
    }, []);

    const productCount = useMemo(
        () => sections.reduce((total, section) => total + section.products.length, 0),
        [sections]
    );

    // Mirrors the backend's MpmSelection rules so a node cannot be saved in a state that
    // would only fail at send time.
    const problem = useMemo(() => {
        if (!body.trim()) return "Body text is required";
        if (!header.trim()) return "Header is required for a product list";
        if (productCount === 0) return "Add at least one product";
        if (productCount > MAX_PRODUCTS) return `WhatsApp allows ${MAX_PRODUCTS} products; you have ${productCount}`;
        if (sections.length > MAX_SECTIONS) return `WhatsApp allows ${MAX_SECTIONS} sections`;
        if (sections.some((s) => s.products.length === 0)) return "Every section needs at least one product";
        if (sections.some((s) => !s.title.trim())) return "Every section needs a title";
        return null;
    }, [header, body, productCount, sections]);

    const updateSection = (index: number, patch: Partial<Section>) => {
        setSections((current) => current.map((section, i) => (i === index ? { ...section, ...patch } : section)));
    };

    const toggleProduct = (index: number, product: CatalogProduct, productCatalogId: string) => {
        if (!catalogId) setCatalogId(productCatalogId);
        setSections((current) => {
            const inThisSection = current[index].products.some((p) => p.retailerId === product.retailerId);
            return current.map((section, i) => {
                if (i === index) {
                    return {
                        ...section,
                        products: inThisSection
                            ? section.products.filter((p) => p.retailerId !== product.retailerId)
                            : [...section.products, product],
                    };
                }
                // A product lives in one section at a time — adding it here moves it out of any other.
                if (!inThisSection && section.products.some((p) => p.retailerId === product.retailerId)) {
                    return { ...section, products: section.products.filter((p) => p.retailerId !== product.retailerId) };
                }
                return section;
            });
        });
    };

    const removeSection = (index: number) => {
        const remaining = sections.filter((_, i) => i !== index);
        const next = remaining.length ? remaining : [{ title: "", products: [] }];
        setSections(next);
        if (next.every((s) => s.products.length === 0)) setCatalogId("");
    };

    const handleSave = () => {
        const updatedNodes = nodes.map((node) => {
            if (node.data.id !== data.nodeId) return node;
            return {
                ...node,
                data: {
                    ...node.data,
                    content: (node.data as CanvasNodeData).content.map((c) =>
                        c.id === data.subNodeId
                            ? { ...c, data: { header, body, footer, saveAs, catalogId, sections } }
                            : c
                    ),
                },
            };
        });
        setNodes(updatedNodes as CanvasNode[]);
        onClose();
    };

    const inputStyle: React.CSSProperties = {
        width: "100%", padding: "8px 10px", borderRadius: "8px",
        border: "1px solid #e2e8f0", fontSize: "13px",
    };

    return (
        <div style={{ padding: "8px 4px", display: "flex", flexDirection: "column", gap: "14px" }}>
            <div>
                <label className="label-premium">Header <span style={{ color: "#ef4444" }}>*</span></label>
                <input
                    style={inputStyle}
                    value={header}
                    maxLength={60}
                    placeholder="Our Products"
                    onChange={(e) => setHeader(e.target.value)}
                />
            </div>

            <div>
                <label className="label-premium">Body <span style={{ color: "#ef4444" }}>*</span></label>
                <textarea
                    style={{ ...inputStyle, minHeight: "60px", resize: "vertical" }}
                    value={body}
                    maxLength={1024}
                    placeholder="Choose from these products"
                    onChange={(e) => setBody(e.target.value)}
                />
            </div>

            <div>
                <label className="label-premium">Footer</label>
                <input style={inputStyle} value={footer} maxLength={60} onChange={(e) => setFooter(e.target.value)} />
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <label className="label-premium" style={{ margin: 0 }}>Sections</label>
                <span style={{ fontSize: "11px", color: productCount > MAX_PRODUCTS ? "#dc2626" : "#64748b" }}>
                    {productCount}/{MAX_PRODUCTS} products · {sections.length}/{MAX_SECTIONS} sections
                </span>
            </div>

            {sections.map((section, index) => (
                <div
                    key={index}
                    style={{ border: "1px solid #e2e8f0", borderRadius: "10px", padding: "10px 12px", display: "flex", flexDirection: "column", gap: "8px" }}
                >
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <input
                            style={{ ...inputStyle, flex: 1 }}
                            value={section.title}
                            maxLength={MAX_TITLE}
                            placeholder={`Section title (required, max ${MAX_TITLE})`}
                            onChange={(e) => updateSection(index, { title: e.target.value })}
                        />
                        <button
                            type="button"
                            onClick={() => removeSection(index)}
                            title="Remove section"
                            style={{ border: "1px solid #e2e8f0", background: "white", borderRadius: "8px", padding: "7px", cursor: "pointer", lineHeight: 0 }}
                        >
                            <Trash2 size={14} color="#dc2626" />
                        </button>
                    </div>

                    {section.products.length === 0 ? (
                        <div style={{ fontSize: "11px", color: "#f59e0b" }}>No products yet</div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                            {section.products.map((product) => (
                                <div key={product.retailerId} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    {product.imageUrl ? (
                                        <img src={product.imageUrl} alt={product.productName}
                                             style={{ width: 28, height: 28, borderRadius: 6, objectFit: "cover" }} />
                                    ) : (
                                        <div style={{ width: 28, height: 28, borderRadius: 6, background: "#f1f5f9" }} />
                                    )}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: "12px", color: "#0f172a" }}>{product.productName}</div>
                                        <div style={{ fontSize: "10px", color: "#64748b" }}>SKU: {product.retailerId}</div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => toggleProduct(index, product, catalogId)}
                                        style={{ border: "none", background: "transparent", cursor: "pointer", lineHeight: 0 }}
                                    >
                                        <Trash2 size={13} color="#94a3b8" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={() => setPickerFor(index)}
                        style={{ alignSelf: "flex-start", fontSize: "12px", padding: "5px 10px", borderRadius: "8px", border: "1px solid var(--primary-bg-color)", background: "white", color: "var(--primary-bg-color)", cursor: "pointer" }}
                    >
                        + Add products
                    </button>
                </div>
            ))}

            {sections.length < MAX_SECTIONS && (
                <button
                    type="button"
                    onClick={() => setSections([...sections, { title: "", products: [] }])}
                    style={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", padding: "6px 10px", borderRadius: "8px", border: "1px dashed #cbd5e1", background: "white", color: "#475569", cursor: "pointer" }}
                >
                    <Plus size={13} /> Add section
                </button>
            )}

            <div>
                <label className="label-premium">Save selection as</label>
                <AttributePicker
                    value={saveAs}
                    onChange={setSaveAs}
                    placeholder="— Save to attribute (optional) —"
                    defaultType="OBJECT"
                />
            </div>

            {problem && <div style={{ fontSize: "11px", color: "#dc2626" }}>{problem}</div>}

            <button
                type="button"
                disabled={!!problem}
                onClick={handleSave}
                style={{
                    padding: "8px 0", fontSize: "13px", fontWeight: 500, borderRadius: "8px", border: "none",
                    background: problem ? "#cbd5e1" : "var(--primary-bg-color)", color: "white",
                    cursor: problem ? "not-allowed" : "pointer",
                }}
            >
                Save
            </button>

            <Dialog
                header="Select Products"
                visible={pickerFor !== null}
                modal
                appendTo={document.body}
                maskStyle={{ background: "rgba(0,0,0,0.6)", zIndex: 9998 }}
                contentStyle={{ zIndex: 9999 }}
                style={{ width: "50vw" }}
                onHide={() => setPickerFor(null)}
            >
                <AddProductDialog
                    businessId={businessId}
                    multiple
                    selectedRetailerIds={pickerFor !== null ? sections[pickerFor].products.map((p) => p.retailerId) : []}
                    lockedCatalogId={catalogId || undefined}
                    onSelect={(product, productCatalogId) =>
                        pickerFor !== null && toggleProduct(pickerFor, product, productCatalogId)
                    }
                />
            </Dialog>
        </div>
    );
};

export default ProductListDialog;
